interface QueryEntry<T> {
  value?: T;
  expiresAt: number;
  promise?: Promise<T>;
  controller?: AbortController;
}

const entries = new Map<string, QueryEntry<unknown>>();
let accountScope = "anonymous";
const metrics = {
  hits: 0,
  misses: 0,
  coalesced: 0,
  aborted: 0,
};

export function cachedQuery<T>(
  key: string,
  loader: (signal: AbortSignal) => Promise<T>,
  ttlMilliseconds = 10_000,
): Promise<T> {
  const scopedKey = cacheKey(key);
  const now = Date.now();
  const existing = entries.get(scopedKey) as QueryEntry<T> | undefined;
  if (existing?.promise) {
    metrics.coalesced += 1;
    return existing.promise;
  }
  if (existing && existing.expiresAt > now && "value" in existing) {
    metrics.hits += 1;
    return Promise.resolve(existing.value as T);
  }

  metrics.misses += 1;
  const controller = new AbortController();
  const promise = loader(controller.signal)
    .then((value) => {
      if (entries.get(scopedKey)?.promise === promise) {
        entries.set(scopedKey, {
          value,
          expiresAt: Date.now() + ttlMilliseconds,
        });
      }
      return value;
    })
    .catch((error: unknown) => {
      if (entries.get(scopedKey)?.promise === promise) {
        entries.delete(scopedKey);
      }
      throw error;
    });
  entries.set(scopedKey, { expiresAt: 0, promise, controller });
  return promise;
}

export function invalidateQueries(prefix?: string): void {
  if (!prefix) {
    clearEntries(() => true);
    return;
  }
  const scopedPrefix = cacheKey(prefix);
  clearEntries((key) => key.startsWith(scopedPrefix));
}

export function setQueryAccountScope(accountId: string | null): void {
  const next = accountId?.trim() || "anonymous";
  if (next === accountScope) return;
  clearEntries((key) => !key.startsWith("auth\u0000"));
  accountScope = next;
}

export interface QueryMetrics {
  hits: number;
  misses: number;
  coalesced: number;
  aborted: number;
}

export function queryMetrics(): QueryMetrics {
  return { ...metrics };
}

export function resetQueryMetrics(): void {
  metrics.hits = 0;
  metrics.misses = 0;
  metrics.coalesced = 0;
  metrics.aborted = 0;
}

function cacheKey(key: string): string {
  const scope = key.startsWith("auth:") ? "auth" : accountScope;
  return `${scope}\u0000${key}`;
}

function clearEntries(predicate: (key: string) => boolean): void {
  for (const [key, entry] of entries) {
    if (!predicate(key)) continue;
    if (entry.controller && !entry.controller.signal.aborted) {
      entry.controller.abort();
      metrics.aborted += 1;
    }
    entries.delete(key);
  }
}
